import { adminHttp } from './http';
import { AUTH_ENDPOINTS } from '../constants/api';
import type {
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  RegisterResponse,
  RefreshTokenRequest,
  RefreshTokenResponse,
  User,
} from '../types/auth';
import type { ApiResponse } from '../types/api';

export async function login(
  email: string,
  password: string,
  rememberMe = false,
  signal?: AbortSignal
): Promise<LoginResponse> {
  const response = await adminHttp.post<ApiResponse<LoginResponse>>(
    AUTH_ENDPOINTS.LOGIN,
    { email, password, rememberMe } as LoginRequest,
    { signal }
  );

  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.message || 'Login failed');
  }

  return response.data.data;
}

export async function register(
  data: RegisterRequest,
  signal?: AbortSignal
): Promise<RegisterResponse> {
  const response = await adminHttp.post<ApiResponse<RegisterResponse>>(
    AUTH_ENDPOINTS.REGISTER,
    data,
    { signal }
  );

  if (!response.data.success || !response.data.data) {
    const errors = response.data.errors?.join(', ') || response.data.message;
    throw new Error(errors || 'Registration failed');
  }

  return response.data.data;
}

/**
 * Refresh the access token
 */
export async function refreshToken(
  token: string,
  refreshToken: string,
  signal?: AbortSignal
): Promise<RefreshTokenResponse> {
  const response = await adminHttp.post<ApiResponse<RefreshTokenResponse>>(
    AUTH_ENDPOINTS.REFRESH_TOKEN,
    { token, refreshToken } as RefreshTokenRequest,
    { signal }
  );

  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.message || 'Token refresh failed');
  }

  return response.data.data;
}

/**
 * Get current user profile
 */
export async function getMe(signal?: AbortSignal): Promise<User> {
  const response = await adminHttp.get<ApiResponse<User>>(AUTH_ENDPOINTS.ME, { signal });

  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.message || 'Failed to get user profile');
  }

  return response.data.data;
}

/**
 * Logout (revoke refresh token)
 */
export async function logout(refreshToken?: string, signal?: AbortSignal): Promise<void> {
  try {
    await adminHttp.post<ApiResponse<{ message: string }>>(
      AUTH_ENDPOINTS.LOGOUT,
      { refreshToken },
      { signal }
    );
  } catch (error) {
    // Logout should succeed even if API call fails
    console.error('Logout API call failed:', error);
  }
}

/**
 * Token storage keys
 */
const TOKEN_KEY = 'odc_access_token';
const REFRESH_TOKEN_KEY = 'odc_refresh_token';
const TOKEN_EXPIRY_KEY = 'odc_token_expiry';
const REMEMBER_ME_KEY = 'odc_remember_me';

/**
 * Store auth tokens
 */
export function storeTokens(accessToken: string, refreshToken?: string, expiresAt?: string, rememberMe = false): void {

  if (rememberMe) {
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(TOKEN_EXPIRY_KEY);
  } else {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(TOKEN_EXPIRY_KEY);
  }

  const storage = rememberMe ? localStorage : sessionStorage;

  storage.setItem(TOKEN_KEY, accessToken.trim());
  if (expiresAt) {
    storage.setItem(TOKEN_EXPIRY_KEY, expiresAt.trim());
  }

  if (refreshToken) {
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken.trim());
  }

  localStorage.setItem(REMEMBER_ME_KEY, rememberMe.toString());
}

export function getAccessToken(): string | null {
  const localToken = localStorage.getItem(TOKEN_KEY);
  const sessionToken = sessionStorage.getItem(TOKEN_KEY);
  const token = localToken || sessionToken;

  return token ? token.trim() : null;
}

export function getRefreshToken(): string | null {
  const token = localStorage.getItem(REFRESH_TOKEN_KEY);

  return token ? token.trim() : null;
}

/**
 * Get token expiry (checks both localStorage and sessionStorage)
 */
export function getTokenExpiry(): Date | null {
  const expiry = localStorage.getItem(TOKEN_EXPIRY_KEY) || sessionStorage.getItem(TOKEN_EXPIRY_KEY);
  return expiry ? new Date(expiry) : null;
}

/**
 * Check if remember me was enabled
 */
export function wasRememberMe(): boolean {
  return localStorage.getItem(REMEMBER_ME_KEY) === 'true';
}

/**
 * Check if token is expired or about to expire (within 5 minutes)
 */
export function isTokenExpired(): boolean {
  const expiry = getTokenExpiry();
  if (!expiry) return true;

  const now = new Date();
  const fiveMinutes = 5 * 60 * 1000;
  return expiry.getTime() - now.getTime() < fiveMinutes;
}

/**
 * Clear all stored auth data
 */
export function clearTokens(): void {
  // Clear from both storages
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(TOKEN_EXPIRY_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(REMEMBER_ME_KEY);
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(TOKEN_EXPIRY_KEY);
}

